(() => {
  'use strict';

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const clone = (value) => JSON.parse(JSON.stringify(value));

  const init = (root) => {
    if (!root || root.dataset.ufCanvas730Ready === 'true') return;
    const designer = root.querySelector('[data-uf-designer]');
    const svg = designer?.querySelector('[data-uf-canvas-svg]');
    const list = designer?.querySelector('[data-uf-layer-manager-list]');
    if (!designer || !svg || !list) return;
    root.dataset.ufCanvas730Ready = 'true';
    root.dataset.ufCanvasPatch = '7.3-professional-object-editing';

    const selection = svg.querySelector('[data-uf-canvas-selection]');
    const selectionBox = selection?.querySelector('[data-uf-selection-box]');
    const rotationStem = selection?.querySelector('[data-uf-rotation-stem]');
    const handles = Array.from(selection?.querySelectorAll('[data-uf-handle]') || []);
    const guideX = svg.querySelector('[data-uf-guide-x]');
    const guideY = svg.querySelector('[data-uf-guide-y]');
    const safeZoneNode = svg.querySelector('[data-uf-safe-zone], .uf-designer__safe-zone');
    const currentSafeZone = () => {
      const x = Number(safeZoneNode?.getAttribute('x') || 58);
      const y = Number(safeZoneNode?.getAttribute('y') || 48);
      const width = Number(safeZoneNode?.getAttribute('width') || 484);
      const height = Number(safeZoneNode?.getAttribute('height') || 264);
      return { x, y, width, height, cx: x + width / 2, cy: y + height / 2 };
    };
    const undoButton = designer.querySelector('[data-uf-canvas-undo]');
    const redoButton = designer.querySelector('[data-uf-canvas-redo]');
    const zoomInput = designer.querySelector('[data-uf-canvas-zoom]');
    const zoomOutput = designer.querySelector('[data-uf-canvas-zoom-output]');
    const stage = designer.querySelector('[data-uf-canvas-stage]');
    const status = designer.querySelector('[data-uf-canvas-status]');
    const designJson = designer.querySelector('[data-uf-design-json]');
    const resetButton = designer.querySelector('[data-uf-designer-reset]');
    const objectToolbar = designer.querySelector('[data-uf-object-toolbar]');
    const toolbarLabel = designer.querySelector('[data-uf-object-toolbar-label]');
    const rotationOutput = designer.querySelector('[data-uf-object-rotation-output]');
    const engravingTextInput = root.querySelector('[data-uf-engraving-text]');
    const artworkInput = root.querySelector('[data-uf-artwork-input]');
    const quickLayerButtons = Array.from(designer.querySelectorAll('[data-uf-layer]'));

    const nodes = new Map();
    const instanceSide = designer.dataset.ufSideLabel === 'back' ? 'back' : 'front';
    const state = { selected: '', counter: 0, objects: {}, activeSide: instanceSide };
    const sideStore = { front: null, back: null };
    const historyStore = { front: null, back: null };
    const baseIds = new Set(['text', 'artwork', 'qr']);
    let history = [];
    let historyIndex = -1;
    let gesture = null;
    let historyTimer = null;
    let isRestoringHistory = false;

    const baseType = (id) => { const value = String(id); if (value.startsWith('icon-')) return 'icon'; return value.split('-copy-')[0]; };
    const defaultLabel = (type) => ({ text: 'Text', artwork: 'Artwork', qr: 'QR code' }[type] || 'Layer');

    const nodeStartsVisible = (node, id) => {
      const type = baseType(id);
      if (type === 'artwork' || type === 'qr') {
        const image = node.querySelector('image');
        const href = image?.getAttribute('href') || image?.getAttributeNS('http://www.w3.org/1999/xlink', 'href') || '';
        return Boolean(href && !image?.hidden && image?.getAttribute('display') !== 'none');
      }
      if (type === 'text') {
        const input = root.querySelector('[data-uf-engraving-text]');
        return Boolean(String(input?.value || '').trim());
      }
      return true;
    };

    const makeObjectState = (node, id) => ({
      id,
      type: baseType(id),
      label: node.dataset.ufObjectLabel || defaultLabel(baseType(id)),
      x: 0,
      y: 0,
      scale: 1,
      rotation: 0,
      visible: nodeStartsVisible(node, id),
      locked: false,
      duplicate: id.includes('-copy-')
    });

    const registerNode = (node, id) => {
      node.dataset.ufCanvasObject = id;
      nodes.set(id, node);
      if (!state.objects[id]) state.objects[id] = makeObjectState(node, id);
      node.style.cursor = 'move';
      node.addEventListener('pointerdown', (event) => {
        const item = state.objects[id];
        if (!item || !item.visible || item.locked) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (id === 'text' && historyTimer) {
          window.clearTimeout(historyTimer);
          historyTimer = null;
          commitHistory();
        }
        selectObject(id);
        const point = pointFromEvent(event);
        gesture = { type: 'move', id, pointerId: event.pointerId, start: point, original: clone(item) };
        node.setPointerCapture?.(event.pointerId);
      }, true);
    };

    svg.querySelectorAll('[data-uf-canvas-object]').forEach((node) => registerNode(node, node.dataset.ufCanvasObject));

    const pointFromEvent = (event) => {
      const point = svg.createSVGPoint();
      point.x = event.clientX;
      point.y = event.clientY;
      const matrix = svg.getScreenCTM();
      return matrix ? point.matrixTransform(matrix.inverse()) : { x: 0, y: 0 };
    };

    const getBaseBox = (id) => {
      const node = nodes.get(id);
      if (!node) return null;
      try {
        const box = node.getBBox();
        if (box.width > 0 && box.height > 0) return box;
      } catch (_error) {}
      const type = state.objects[id]?.type || baseType(id);
      if (type === 'artwork') return { x: 220, y: 88, width: 160, height: 120 };
      if (type === 'qr') return { x: 245, y: 120, width: 110, height: 110 };
      return { x: 220, y: 150, width: 160, height: 60 };
    };

    const contentVisible = (id) => {
      const item = state.objects[id];
      const node = nodes.get(id);
      if (!item || !node || !item.visible) return false;
      const child = node.querySelector('image, text, path, circle, ellipse, polygon, polyline, rect, line');
      return Boolean(child && !child.hidden && child.getAttribute('display') !== 'none');
    };

    const applyTransform = (id) => {
      const node = nodes.get(id);
      const item = state.objects[id];
      const box = getBaseBox(id);
      if (!node || !item || !box) return;
      const cx = box.x + box.width / 2;
      const cy = box.y + box.height / 2;
      node.hidden = !item.visible;
      node.setAttribute('aria-hidden', item.visible ? 'false' : 'true');
      const contentChild = node.querySelector('image, text, path, circle, ellipse, polygon, polyline, rect, line');
      if (contentChild) contentChild.hidden = !item.visible;
      node.style.pointerEvents = item.locked ? 'none' : '';
      node.style.cursor = item.locked ? 'not-allowed' : 'move';
      node.setAttribute('transform', `translate(${item.x.toFixed(2)} ${item.y.toFixed(2)}) rotate(${item.rotation.toFixed(2)} ${cx.toFixed(2)} ${cy.toFixed(2)}) translate(${cx.toFixed(2)} ${cy.toFixed(2)}) scale(${item.scale.toFixed(4)}) translate(${-cx.toFixed(2)} ${-cy.toFixed(2)})`);
    };

    const transformedBox = (id) => {
      const box = getBaseBox(id);
      const item = state.objects[id];
      if (!box || !item) return null;
      const width = box.width * item.scale;
      const height = box.height * item.scale;
      const cx = box.x + box.width / 2 + item.x;
      const cy = box.y + box.height / 2 + item.y;
      return { x: cx - width / 2, y: cy - height / 2, width, height, cx, cy };
    };

    const updateSelection = () => {
      const id = state.selected;
      const item = state.objects[id];
      const box = transformedBox(id);
      if (!selection || !selectionBox || !item || !box || !item.visible || item.locked) {
        if (selection) selection.hidden = true;
        if (objectToolbar) objectToolbar.hidden = true;
        return;
      }
      selection.hidden = false;
      selectionBox.setAttribute('x', box.x);
      selectionBox.setAttribute('y', box.y);
      selectionBox.setAttribute('width', box.width);
      selectionBox.setAttribute('height', box.height);
      selectionBox.setAttribute('transform', `rotate(${item.rotation} ${box.cx} ${box.cy})`);
      const positions = { nw: [box.x, box.y], ne: [box.x + box.width, box.y], sw: [box.x, box.y + box.height], se: [box.x + box.width, box.y + box.height] };
      handles.forEach((handle) => {
        const type = handle.dataset.ufHandle;
        const pos = type === 'rotate' ? [box.cx, box.y - 30] : positions[type];
        if (pos) { handle.setAttribute('cx', pos[0]); handle.setAttribute('cy', pos[1]); }
        handle.setAttribute('transform', `rotate(${item.rotation} ${box.cx} ${box.cy})`);
      });
      if (rotationStem) {
        rotationStem.setAttribute('x1', box.cx); rotationStem.setAttribute('y1', box.y);
        rotationStem.setAttribute('x2', box.cx); rotationStem.setAttribute('y2', box.y - 22);
        rotationStem.setAttribute('transform', `rotate(${item.rotation} ${box.cx} ${box.cy})`);
      }
      if (objectToolbar) objectToolbar.hidden = false;
      if (toolbarLabel) toolbarLabel.textContent = item.label;
      if (rotationOutput) rotationOutput.textContent = `${Math.round(item.rotation)}°`;
      if (status) status.textContent = `${item.label} selected. Drag to move; use corner handles to resize and the round top handle to rotate.`;
    };

    const orderedIds = () => Array.from(list.querySelectorAll('[data-uf-layer-row]')).map((row) => row.dataset.ufLayerRow).filter((id) => state.objects[id]);

    const syncSvgOrder = () => {
      orderedIds().slice().reverse().forEach((id) => {
        const node = nodes.get(id);
        if (node && selection) svg.insertBefore(node, selection);
      });
    };

    const mergeDesignData = () => {
      if (!designJson) return;
      let data = {};
      try { data = JSON.parse(designJson.value || '{}'); } catch (_error) {}
      data.version = '7.3';
      data.canvas = {
        coordinateSystem: '600x360',
        safeZone: (() => { const z = currentSafeZone(); return { x:z.x, y:z.y, width:z.width, height:z.height }; })(),
        side: instanceSide,
        design: snapshotSide()
      };
      designJson.value = JSON.stringify(data);
    };

    const nodeMarkup = (id) => {
      const node = nodes.get(id);
      return node && !baseIds.has(id) ? node.outerHTML : '';
    };

    const snapshotSide = () => ({
      selected: state.selected,
      counter: state.counter,
      objects: clone(state.objects),
      order: orderedIds(),
      markup: Object.fromEntries(orderedIds().filter((id) => !baseIds.has(id)).map((id) => [id, nodeMarkup(id)])),
      textValue: String(engravingTextInput?.value || '')
    });

    const emptySide = () => ({
      selected: '', counter: 0,
      objects: Object.fromEntries(Array.from(baseIds).filter((id) => nodes.has(id)).map((id) => [id, makeObjectState(nodes.get(id), id)])),
      order: ['text', 'qr', 'artwork'], markup: {}, textValue: ''
    });

    const removeCustomNodes = () => {
      Array.from(nodes.keys()).forEach((id) => {
        if (!baseIds.has(id)) { nodes.get(id)?.remove(); nodes.delete(id); }
      });
    };

    const restoreSide = (side) => {
      const saved = clone(sideStore[side] || emptySide());

      // Sync the shared text field to this side's own saved value BEFORE any
      // fallback object-state construction below, since that construction can
      // read the field's current value to decide default visibility.
      if (engravingTextInput && engravingTextInput.value !== (saved.textValue || '')) {
        isRestoringHistory = true;
        engravingTextInput.value = saved.textValue || '';
        engravingTextInput.dispatchEvent(new Event('input', { bubbles: true }));
        isRestoringHistory = false;
      }

      removeCustomNodes();
      Object.entries(saved.markup || {}).forEach(([id, html]) => {
        if (!html) return;
        const holder = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        holder.innerHTML = html;
        const node = holder.firstElementChild;
        if (node) { node.removeAttribute('transform'); svg.insertBefore(node, selection); registerNode(node, id); }
      });
      state.activeSide = side;
      state.selected = saved.selected || 'text';
      state.counter = saved.counter || 0;
      state.objects = saved.objects || {};
      Array.from(baseIds).forEach((id) => { if (nodes.has(id) && !state.objects[id]) state.objects[id] = makeObjectState(nodes.get(id), id); });
      rebuildRows((saved.order || ['text','qr','artwork']).filter((id) => state.objects[id]));

      // Restore this side's own undo history instead of always starting a
      // fresh one-entry history, which previously made Undo/Redo go gray
      // immediately after every side switch.
      const storedHistory = historyStore[side];
      if (storedHistory) {
        history = storedHistory.history;
        historyIndex = storedHistory.historyIndex;
        updateHistoryButtons();
      } else {
        history = []; historyIndex = -1;
        commitHistory();
      }
      render();
    };

    const renderRows = () => {
      list.querySelectorAll('[data-uf-layer-row]').forEach((row) => {
        const id = row.dataset.ufLayerRow;
        const item = state.objects[id];
        if (!item) return;
        row.classList.toggle('is-selected', id === state.selected);
        row.classList.toggle('is-hidden', !item.visible);
        row.classList.toggle('is-locked', item.locked);
        row.querySelector('[data-uf-layer-name]').textContent = item.label;
        const typeNode = row.querySelector('small');
        if (typeNode) typeNode.textContent = item.type === 'text' ? 'Text layer' : item.type === 'artwork' ? 'Artwork layer' : item.type === 'qr' ? 'QR layer' : 'Design layer';
        const visibility = row.querySelector('[data-uf-layer-visibility]');
        const lock = row.querySelector('[data-uf-layer-lock]');
        if (visibility) { visibility.setAttribute('aria-pressed', String(item.visible)); visibility.textContent = item.visible ? 'Hide' : 'Show'; }
        if (lock) { lock.setAttribute('aria-pressed', String(item.locked)); lock.textContent = item.locked ? 'Unlock' : 'Lock'; }
      });
    };

    const render = () => {
      Object.keys(state.objects).forEach(applyTransform);
      syncSvgOrder();
      renderRows();
      updateSelection();
      mergeDesignData();
      designer.dispatchEvent(new CustomEvent('uf:canvas-updated', { detail: { side: state.activeSide, selected: state.selected } }));
    };

    const snapshot = () => ({
      selected: state.selected,
      counter: state.counter,
      objects: clone(state.objects),
      order: orderedIds(),
      markup: Object.fromEntries(orderedIds().filter((id) => !baseIds.has(id)).map((id) => [id, nodeMarkup(id)])),
      textValue: String(engravingTextInput?.value || '')
    });
    const commitHistory = () => {
      const next = snapshot();
      if (historyIndex >= 0 && JSON.stringify(next) === JSON.stringify(history[historyIndex])) return;
      history.splice(historyIndex + 1);
      history.push(next);
      if (history.length > 50) history.shift();
      historyIndex = history.length - 1;
      updateHistoryButtons();
    };
    const updateHistoryButtons = () => {
      if (undoButton) undoButton.disabled = historyIndex <= 0;
      if (redoButton) redoButton.disabled = historyIndex >= history.length - 1;
    };

    const createRow = (id) => {
      const item = state.objects[id];
      const row = document.createElement('li');
      row.className = 'uf-layer-manager__row';
      row.dataset.ufLayerRow = id;
      row.innerHTML = `<button type="button" class="uf-layer-manager__select" data-uf-layer-select><span class="uf-layer-manager__grip" aria-hidden="true">⋮⋮</span><span data-uf-layer-name></span><small>${item.type}</small></button><div class="uf-layer-manager__actions"><button type="button" data-uf-layer-visibility aria-label="Toggle layer visibility"></button><button type="button" data-uf-layer-lock aria-label="Toggle layer lock"></button><button type="button" data-uf-layer-duplicate aria-label="Duplicate layer">Duplicate</button><button type="button" data-uf-layer-delete aria-label="Delete layer">Delete</button></div>`;
      return row;
    };

    const rebuildRows = (order) => {
      list.innerHTML = '';
      order.forEach((id) => { if (state.objects[id]) list.appendChild(createRow(id)); });
    };

    const restoreHistory = (index) => {
      const saved = history[index];
      if (!saved) return;
      Object.keys(state.objects).forEach((id) => {
        if (!saved.objects[id] && state.objects[id]?.duplicate) { nodes.get(id)?.remove(); nodes.delete(id); }
      });
      Object.keys(saved.objects).forEach((id) => {
        if (!nodes.has(id) && saved.objects[id].duplicate) {
          const html = saved.markup?.[id];
          if (html) { const holder = document.createElementNS('http://www.w3.org/2000/svg', 'g'); holder.innerHTML = html; const node = holder.firstElementChild; if (node) { node.removeAttribute('transform'); svg.insertBefore(node, selection); registerNode(node, id); } }
          else { const source = nodes.get(saved.objects[id].type); if (source) registerNode(createDuplicateNode(source, id), id); }
        }
      });
      state.selected = saved.selected;
      state.counter = saved.counter;
      state.objects = clone(saved.objects);
      rebuildRows(saved.order.filter((id) => state.objects[id]));
      historyIndex = index;

      // Text content is part of the design lifecycle, not just the SVG transform.
      // Restoring the input also lets the personalizer repaint the SVG text.
      if (engravingTextInput) {
        const restoredText = String(saved.textValue || '');
        if (engravingTextInput.value !== restoredText) {
          isRestoringHistory = true;
          engravingTextInput.value = restoredText;
          engravingTextInput.dispatchEvent(new Event('input', { bubbles: true }));
          isRestoringHistory = false;
        }
      }
      render();
      designer.dispatchEvent(new CustomEvent('uf:canvas-history-restored', {
        bubbles: true,
        detail: { artworkVisible: Boolean(state.objects.artwork?.visible), side: state.activeSide }
      }));
      updateHistoryButtons();
    };

    const announceSelection = (id) => {
      const item = state.objects[id];
      if (!item) return;
      designer.dispatchEvent(new CustomEvent('uf:canvas-object-selected', {
        bubbles: true,
        detail: { id, type: item.type, side: state.activeSide }
      }));
    };

    // Selection state must not depend on the SVG having completed a separate
    // module's render cycle. The state is committed first; the transform box
    // is then refreshed as the SVG text becomes measurable.
    const selectObject = (id) => {
      const item = state.objects[id];
      if (!item || !item.visible || item.locked) return false;
      state.selected = id;
      renderRows();
      updateSelection();
      mergeDesignData();
      announceSelection(id);
      return true;
    };

    const selectAfterRender = (id, maxFrames = 8) => {
      if (!selectObject(id)) return;
      let frame = 0;
      const refresh = () => {
        if (state.selected !== id) return;
        render();
        const node = nodes.get(id);
        let measurable = false;
        try {
          const box = node?.getBBox();
          measurable = Boolean(box && box.width > 0 && box.height > 0);
        } catch (_error) {}
        frame += 1;
        if (!measurable && frame < maxFrames) window.requestAnimationFrame(refresh);
      };
      window.requestAnimationFrame(refresh);
    };

    const clearSelection = (reason = 'outside') => {
      if (gesture || !state.selected) return;
      state.selected = '';
      renderRows();
      updateSelection();
      if (selection) selection.hidden = true;
      if (guideX) guideX.hidden = true;
      if (guideY) guideY.hidden = true;
      if (status) status.textContent = 'Nothing selected. Click text, artwork, a QR code, or a symbol to edit it.';
      mergeDesignData();
      designer.dispatchEvent(new CustomEvent('uf:canvas-deselect', {
        bubbles: true,
        detail: { reason, side: state.activeSide }
      }));
    };

    const createDuplicateNode = (source, id) => {
      const node = source.cloneNode(true);
      node.dataset.ufCanvasObject = id;
      node.dataset.ufObjectLabel = `${source.dataset.ufObjectLabel || defaultLabel(baseType(id))} copy`;
      node.querySelectorAll('[data-uf-svg-text],[data-uf-svg-artwork],[data-uf-svg-qr]').forEach((child) => {
        child.removeAttribute('data-uf-svg-text'); child.removeAttribute('data-uf-svg-artwork'); child.removeAttribute('data-uf-svg-qr');
      });
      node.removeAttribute('transform');
      svg.insertBefore(node, selection);
      return node;
    };

    const duplicateLayer = (id) => {
      const source = nodes.get(id);
      const original = state.objects[id];
      if (!source || !original || !contentVisible(id)) return;
      state.counter += 1;
      const newId = `${original.type}-copy-${state.counter}`;
      const node = createDuplicateNode(source, newId);
      registerNode(node, newId);
      state.objects[newId] = { ...clone(original), id: newId, label: `${original.label} copy`, x: original.x + 18, y: original.y + 18, duplicate: true, locked: false, visible: true };
      const sourceRow = list.querySelector(`[data-uf-layer-row="${CSS.escape(id)}"]`);
      const row = createRow(newId);
      sourceRow ? sourceRow.before(row) : list.prepend(row);
      state.selected = newId;
      commitHistory(); render();
    };

    const deleteLayer = (id) => {
      const item = state.objects[id];
      if (!item) return;
      if (item.duplicate) { nodes.get(id)?.remove(); nodes.delete(id); delete state.objects[id]; list.querySelector(`[data-uf-layer-row="${CSS.escape(id)}"]`)?.remove(); }
      else { item.visible = false; item.locked = true; }
      state.selected = orderedIds().find((candidate) => state.objects[candidate]?.visible) || '';
      commitHistory(); render();
    };

    const moveLayer = (id, direction) => {
      const row = list.querySelector(`[data-uf-layer-row="${CSS.escape(id)}"]`);
      if (!row) return;
      if (direction === 'up' && row.previousElementSibling) list.insertBefore(row, row.previousElementSibling);
      if (direction === 'down' && row.nextElementSibling) list.insertBefore(row.nextElementSibling, row);
      commitHistory(); render();
    };

    // Initial rows: top entry is visually in front.
    rebuildRows(['text', 'qr', 'artwork'].filter((id) => state.objects[id]));

    list.addEventListener('click', (event) => {
      const row = event.target.closest('[data-uf-layer-row]');
      if (!row) return;
      const id = row.dataset.ufLayerRow;
      const item = state.objects[id];
      if (!item) return;
      if (event.target.closest('[data-uf-layer-select]')) selectObject(id);
      else if (event.target.closest('[data-uf-layer-visibility]')) { item.visible = !item.visible; if (item.visible) item.locked = false; commitHistory(); render(); }
      else if (event.target.closest('[data-uf-layer-lock]')) { item.locked = !item.locked; commitHistory(); render(); }
      else if (event.target.closest('[data-uf-layer-duplicate]')) duplicateLayer(id);
      else if (event.target.closest('[data-uf-layer-delete]')) deleteLayer(id);
    });

    designer.querySelector('[data-uf-layer-up]')?.addEventListener('click', () => moveLayer(state.selected, 'up'));
    designer.querySelector('[data-uf-layer-down]')?.addEventListener('click', () => moveLayer(state.selected, 'down'));

    quickLayerButtons.forEach((button) => button.addEventListener('click', () => {
      const type = button.dataset.ufLayer;
      const id = orderedIds().find((candidate) => state.objects[candidate]?.type === type && state.objects[candidate]?.visible) || type;
      window.requestAnimationFrame(() => selectObject(id));
    }));

    // Clicking an unused part of the canvas clears the active layer and hides
    // its selection outline/resize controls. Object and handle clicks stop
    // propagation, so they remain selected as expected.
    const clearCanvasSelection = (event) => {
      if (gesture) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-uf-handle], [data-uf-canvas-object]')) return;
      clearSelection('blank-canvas');
    };

    // Blank areas inside the SVG clear the transform box immediately.
    svg.addEventListener('pointerdown', clearCanvasSelection);

    // A page-level bubbling listener clears selection when the customer clicks
    // anywhere outside a design object. Object/handle pointer handlers stop
    // propagation, so direct object selection is preserved on mouse and touch.
    document.addEventListener('pointerdown', (event) => {
      if (!root.isConnected || gesture || !state.selected) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-uf-canvas-object], [data-uf-handle]')) return;
      // Only actual controls (inputs, buttons, selects, labels) keep the
      // current selection while in use. Blank space anywhere else on the
      // page, including unused areas of the design tool's own panels, now
      // clears the selection like clicking blank canvas does.
      if (target.closest('input, select, textarea, button, label, [role="tab"], [data-uf-object-toolbar]')) return;
      clearSelection('page');
    });

    handles.forEach((handle) => handle.addEventListener('pointerdown', (event) => {
      const id = state.selected;
      const item = state.objects[id];
      const box = transformedBox(id);
      if (!item || item.locked || !item.visible || !box) return;
      event.preventDefault(); event.stopPropagation();
      const point = pointFromEvent(event);
      gesture = { type: handle.dataset.ufHandle === 'rotate' ? 'rotate' : 'resize', id, pointerId: event.pointerId, original: clone(item), center: { x: box.cx, y: box.cy }, startDistance: Math.max(1, Math.hypot(point.x - box.cx, point.y - box.cy)), startAngle: Math.atan2(point.y - box.cy, point.x - box.cx) * 180 / Math.PI };
      handle.setPointerCapture?.(event.pointerId);
    }));

    const snapMove = (id, x, y) => {
      const box = getBaseBox(id);
      const item = state.objects[id];
      if (!box || !item) return { x, y };
      const safe = currentSafeZone();
      const width = box.width * item.scale;
      const height = box.height * item.scale;
      const cx = box.x + box.width / 2 + x;
      const cy = box.y + box.height / 2 + y;
      const left = cx - width / 2;
      const right = cx + width / 2;
      const top = cy - height / 2;
      const bottom = cy + height / 2;
      const threshold = 7;
      let nextX = x;
      let nextY = y;
      let snapX = null;
      let snapY = null;

      const xTargets = [
        { delta: safe.cx - cx, line: safe.cx },
        { delta: safe.x - left, line: safe.x },
        { delta: safe.x + safe.width - right, line: safe.x + safe.width }
      ].sort((a,b) => Math.abs(a.delta) - Math.abs(b.delta));
      const yTargets = [
        { delta: safe.cy - cy, line: safe.cy },
        { delta: safe.y - top, line: safe.y },
        { delta: safe.y + safe.height - bottom, line: safe.y + safe.height }
      ].sort((a,b) => Math.abs(a.delta) - Math.abs(b.delta));

      if (Math.abs(xTargets[0].delta) <= threshold) { nextX += xTargets[0].delta; snapX = xTargets[0].line; }
      if (Math.abs(yTargets[0].delta) <= threshold) { nextY += yTargets[0].delta; snapY = yTargets[0].line; }

      if (guideX) {
        guideX.hidden = snapX === null;
        if (snapX !== null) { guideX.setAttribute('x1', snapX); guideX.setAttribute('x2', snapX); }
      }
      if (guideY) {
        guideY.hidden = snapY === null;
        if (snapY !== null) { guideY.setAttribute('y1', snapY); guideY.setAttribute('y2', snapY); }
      }
      return { x: nextX, y: nextY };
    };


    svg.addEventListener('pointermove', (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const point = pointFromEvent(event);
      const item = state.objects[gesture.id];
      if (!item || item.locked) return;
      if (gesture.type === 'move') {
        const next = snapMove(gesture.id, gesture.original.x + point.x - gesture.start.x, gesture.original.y + point.y - gesture.start.y);
        item.x = clamp(next.x, -450, 450); item.y = clamp(next.y, -280, 280);
      } else if (gesture.type === 'resize') {
        item.scale = clamp(gesture.original.scale * Math.max(1, Math.hypot(point.x - gesture.center.x, point.y - gesture.center.y)) / gesture.startDistance, 0.25, 4);
      } else {
        const angle = Math.atan2(point.y - gesture.center.y, point.x - gesture.center.x) * 180 / Math.PI;
        let rotation = gesture.original.rotation + angle - gesture.startAngle;
        if (event.shiftKey) rotation = Math.round(rotation / 15) * 15;
        item.rotation = Math.round(rotation * 10) / 10;
      }
      render();
    });

    const endGesture = (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      gesture = null;
      if (guideX) guideX.hidden = true;
      if (guideY) guideY.hidden = true;
      commitHistory(); render();
    };
    svg.addEventListener('pointerup', endGesture);
    svg.addEventListener('pointercancel', endGesture);

    undoButton?.addEventListener('click', () => restoreHistory(historyIndex - 1));
    redoButton?.addEventListener('click', () => restoreHistory(historyIndex + 1));
    zoomInput?.addEventListener('input', () => { const zoom = Number(zoomInput.value || 100); if (stage) stage.style.setProperty('--uf-canvas-zoom', String(zoom / 100)); if (zoomOutput) zoomOutput.textContent = `${zoom}%`; });

    document.addEventListener('keydown', (event) => {
      if (!root.isConnected) return;
      const tag = document.activeElement?.tagName;
      const editingField = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && !editingField) {
        event.preventDefault();
        restoreHistory(event.shiftKey ? historyIndex + 1 : historyIndex - 1);
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y' && !editingField) {
        event.preventDefault(); restoreHistory(historyIndex + 1); return;
      }
      if (!state.selected || editingField) return;
      const item = state.objects[state.selected];
      if (!item || item.locked || !item.visible) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd') {
        event.preventDefault(); duplicateLayer(state.selected); return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault(); deleteLayer(state.selected); return;
      }
      const steps = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
      if (!steps[event.key]) return;
      event.preventDefault();
      const step = event.shiftKey ? 10 : 2;
      item.x += steps[event.key][0] * step; item.y += steps[event.key][1] * step;
      render(); window.clearTimeout(historyTimer); historyTimer = window.setTimeout(commitHistory, 180);
    });


    objectToolbar?.addEventListener('click', (event) => {
      const action = event.target.closest('[data-uf-object-action]')?.dataset.ufObjectAction;
      const id = state.selected;
      const item = state.objects[id];
      if (!action || !id || !item) return;
      if (action === 'duplicate') duplicateLayer(id);
      else if (action === 'forward') moveLayer(id, 'up');
      else if (action === 'backward') moveLayer(id, 'down');
      else if (action === 'lock') { item.locked = !item.locked; commitHistory(); render(); }
      else if (action === 'hide') { item.visible = false; state.selected = ''; commitHistory(); render(); }
      else if (action === 'delete') deleteLayer(id);
      else if (action === 'center') {
        const box = transformedBox(id); const safe = currentSafeZone();
        if (box) { item.x += safe.cx - box.cx; item.y += safe.cy - box.cy; commitHistory(); render(); }
      } else if (action === 'rotate-reset') { item.rotation = 0; commitHistory(); render(); }
    });

    resetButton?.addEventListener('click', () => {
      // Let the layer/data module clear uploaded media, QR content and both-side
      // caches before rebuilding the canvas state. The event is synchronous.
      designer.dispatchEvent(new CustomEvent('uf:full-reset', { bubbles: true }));
      if (engravingTextInput && engravingTextInput.value) {
        isRestoringHistory = true;
        engravingTextInput.value = '';
        engravingTextInput.dispatchEvent(new Event('input', { bubbles: true }));
        isRestoringHistory = false;
      }
      Array.from(nodes.keys()).forEach((id) => {
        if (!baseIds.has(id)) { nodes.get(id)?.remove(); nodes.delete(id); }
      });
      state.objects = {};
      svg.querySelectorAll('[data-uf-canvas-object]').forEach((node) => {
        const id = node.dataset.ufCanvasObject;
        if (!id.includes('-copy-')) state.objects[id] = makeObjectState(node, id);
      });
      state.selected = '';
      state.counter = 0;
      sideStore.front = emptySide();
      sideStore.back = emptySide();
      historyStore.front = null;
      historyStore.back = null;
      rebuildRows(['text', 'qr', 'artwork'].filter((id) => state.objects[id]));
      history = [];
      historyIndex = -1;
      commitHistory();
      render();
      clearSelection('reset');
    });

    engravingTextInput?.addEventListener('input', (event) => {
      if (isRestoringHistory) return;
      const hasText = Boolean(String(event.currentTarget?.value || '').trim());
      const textObject = state.objects.text;
      if (!textObject) return;
      textObject.visible = hasText;
      textObject.locked = false;
      const enteredText = String(event.currentTarget?.value || '').trim().replace(/\s+/g, ' ');
      textObject.label = enteredText ? `Text: ${enteredText.slice(0, 28)}${enteredText.length > 28 ? '…' : ''}` : 'Text';

      if (hasText) {
        // Commit selection synchronously on the first keystroke. This removes
        // the previous dependency on clicking Artwork/Text to wake selection.
        selectAfterRender('text');
      } else {
        render();
        if (state.selected === 'text') clearSelection('empty-text');
      }

      // Keep the typed value in the same history timeline as move/scale/rotate.
      // A short debounce treats normal typing as one edit. Pointer-down flushes
      // this pending state before a drag so Undo first restores position, then
      // removes the text when it reaches the original blank snapshot.
      window.clearTimeout(historyTimer);
      historyTimer = window.setTimeout(() => {
        historyTimer = null;
        commitHistory();
      }, 220);
    });
    designer.addEventListener('uf:artwork-state-change', (event) => {
      const artwork = state.objects.artwork;
      if (!artwork) return;
      const hasArtwork = Boolean(event.detail?.hasArtwork);
      artwork.visible = hasArtwork;
      artwork.locked = false;
      if (hasArtwork) {
        selectAfterRender('artwork');
      } else {
        if (state.selected === 'artwork') state.selected = '';
        render();
      }
      commitHistory();
      render();
    });

    designer.addEventListener('uf:artwork-history-visibility', (event) => {
      const artwork = state.objects.artwork;
      if (!artwork) return;
      artwork.visible = Boolean(event.detail?.visible);
      artwork.locked = false;
      if (!artwork.visible && state.selected === 'artwork') state.selected = '';
      render();
    });
    artworkInput?.addEventListener('change', () => {
      const artwork = state.objects.artwork;
      const file = artworkInput.files?.[0];
      if (artwork && file) artwork.label = `Artwork: ${file.name.slice(0, 30)}${file.name.length > 30 ? '…' : ''}`;
      render();
    });
    designer.querySelector('[data-uf-qr-content]')?.addEventListener('input', (event) => setTimeout(() => {
      const value = String(event.currentTarget?.value || '').trim();
      state.objects.qr.visible = Boolean(value);
      state.objects.qr.label = value ? `QR: ${value.slice(0, 26)}${value.length > 26 ? '…' : ''}` : 'QR code';
      if (value) selectObject('qr');
      render();
    }, 350));
    root.closest('form')?.addEventListener('submit', mergeDesignData);

    designer.querySelectorAll('[data-uf-side]').forEach((button) => {
      button.addEventListener('click', () => {
        const nextSide = button.dataset.ufSide || 'front';
        if (nextSide === state.activeSide) return;
        sideStore[state.activeSide] = snapshotSide();
        historyStore[state.activeSide] = { history, historyIndex };
        window.requestAnimationFrame(() => restoreSide(nextSide));
      });
    });

    designer.addEventListener('uf:add-library-icon', (event) => {
      const detail = event.detail || {};
      if (!detail.svg) return;
      state.counter += 1;
      const id = `icon-${detail.key || 'design'}-${state.counter}`;
      const holder = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      holder.innerHTML = `<g data-uf-canvas-object="${id}" data-uf-object-label="${String(detail.label || 'Design icon').replace(/"/g, '&quot;')}">${detail.svg}</g>`;
      const node = holder.firstElementChild;
      if (!node) return;
      svg.insertBefore(node, selection);
      registerNode(node, id);
      state.objects[id] = { ...makeObjectState(node, id), id, type: 'icon', label: detail.label || 'Design icon', x: 0, y: 0, scale: 1, rotation: 0, visible: true, locked: false, duplicate: true };
      list.prepend(createRow(id));
      state.selected = id;
      commitHistory(); render();
    });

    designer.addEventListener('uf:apply-design-template', (event) => {
      const detail = event.detail || {};
      const input = root.querySelector('[data-uf-engraving-text]');
      if (input && typeof detail.text === 'string') { input.value = detail.text; input.dispatchEvent(new Event('input', { bubbles: true })); }
      const fontSize = designer.querySelector('[data-uf-font-size]');
      if (fontSize && detail.fontSize) { fontSize.value = String(detail.fontSize); fontSize.dispatchEvent(new Event('input', { bubbles: true })); }
      if (detail.icon) designer.dispatchEvent(new CustomEvent('uf:add-library-icon', { detail: detail.icon }));
      window.requestAnimationFrame(render);
    });

    designer.addEventListener('uf:request-deselect', () => clearSelection('approval-preview'));

    designer.addEventListener('uf:smart-command', (event) => {
      const detail = event.detail || {};
      const selectedId = detail.id && state.objects[detail.id] ? detail.id : state.selected;
      const item = state.objects[selectedId];
      const box = selectedId ? transformedBox(selectedId) : null;
      const safe = currentSafeZone();
      if (detail.command === 'center-selected' && item && box) {
        item.x += safe.cx - box.cx;
        item.y += safe.cy - box.cy;
      } else if (detail.command === 'fit-selected' && item && box) {
        const ratio = Math.min(safe.width / Math.max(1, box.width), safe.height / Math.max(1, box.height), 1);
        item.scale = clamp(item.scale * ratio * 0.92, 0.25, 4);
        const nextBox = transformedBox(selectedId);
        if (nextBox) { item.x += safe.cx - nextBox.cx; item.y += safe.cy - nextBox.cy; }
      } else if (detail.command === 'auto-improve') {
        const visible = orderedIds().filter((id) => state.objects[id]?.visible && contentVisible(id));
        const textIds = visible.filter((id) => state.objects[id]?.type === 'text');
        const artworkIds = visible.filter((id) => state.objects[id]?.type === 'artwork' || state.objects[id]?.type === 'icon');
        const qrIds = visible.filter((id) => state.objects[id]?.type === 'qr');
        const setCenter = (id, targetX, targetY, maxW, maxH) => {
          const object = state.objects[id]; const current = transformedBox(id);
          if (!object || !current) return;
          const ratio = Math.min(maxW / Math.max(1, current.width), maxH / Math.max(1, current.height), 1);
          object.scale = clamp(object.scale * ratio, 0.25, 4);
          const resized = transformedBox(id);
          if (resized) { object.x += targetX - resized.cx; object.y += targetY - resized.cy; }
          object.rotation = 0;
        };
        if (visible.length === 1) setCenter(visible[0], safe.cx, safe.cy, safe.width * .89, safe.height * .83);
        else {
          textIds.forEach((id, index) => setCenter(id, safe.cx, safe.y + safe.height * .23 + index * Math.min(48, safe.height * .18), safe.width * .87, Math.min(72, safe.height * .27)));
          artworkIds.forEach((id, index) => setCenter(id, qrIds.length ? safe.x + safe.width * .32 : safe.cx, safe.y + safe.height * .67 + index * 12, qrIds.length ? safe.width * .43 : safe.width * .62, safe.height * .51));
          qrIds.forEach((id, index) => setCenter(id, safe.x + safe.width * .75, safe.y + safe.height * .67 + index * 12, Math.min(125, safe.width * .26), Math.min(125, safe.height * .47)));
        }
      } else if (detail.command === 'reset-rotation' && item) {
        item.rotation = 0;
      } else if (detail.command === 'select' && detail.id && state.objects[detail.id]) {
        selectObject(detail.id);
      } else {
        return;
      }
      commitHistory();
      render();
    });

    sideStore.front = snapshotSide();
    sideStore.back = emptySide();
    commitHistory(); render();
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-uf-personalization-side]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true }); else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
